/**
 * Helpers for the catalog recommendation preview struct array, shared between
 * the query translator (backend) and the preview renderer (frontend).
 *
 * When the catalog model has a secondary display label, each recommended item
 * is serialized as a JSON object whose keys are the catalog display columns.
 * Several warehouses reorder object keys during serialization (Postgres `jsonb`
 * sorts by key length then bytes, Snowflake sorts alphabetically, etc.), so the
 * insertion order is not preserved on the wire. To keep the columns in their
 * intended order (primary label, then secondary), each key is prefixed with its
 * column index. The renderer sorts by the index and strips it to recover the
 * original column name for the header.
 *
 * The encoded key MUST be a valid unquoted SQL identifier ([A-Za-z_][A-Za-z0-9_]*):
 * BigQuery emits it as a raw STRUCT field alias (`ref AS <key>`), where e.g. a
 * leading digit or a colon is a syntax error. Hence the `_<index>__<name>`
 * shape, with non-identifier characters in the column name replaced by `_`.
 */
const ORDERED_PREVIEW_KEY_PATTERN = /^_(\d+)__(.+)$/;

export function encodeOrderedPreviewKey(
  index: number,
  columnName: string,
): string {
  const identifierSafeName = columnName.replace(/[^A-Za-z0-9_]/g, "_");
  return `_${index}__${identifierSafeName}`;
}

export function decodeOrderedPreviewKey(
  key: string,
): { order: number; name: string } | null {
  const match = ORDERED_PREVIEW_KEY_PATTERN.exec(key);
  const [, order, name] = match ?? [];
  if (order === undefined || name === undefined) {
    return null;
  }
  return { order: Number(order), name };
}
